'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchUserProfile,
  linkAnonymousUserData,
  type AuthResponse,
  type AuthUser,
} from '@/services/authService';

const ANONYMOUS_USER_ID_KEY = 'anonymousUserId';
const AUTH_SESSION_KEY = 'razee.auth.session';

interface StoredSession {
  token: string;
  user: AuthUser;
}

interface UserContextValue {
  userId: string | null;
  user: AuthUser | null;
  token: string | null;
  anonymousUserId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  syncAuthSession: (
    session: AuthResponse | StoredSession,
    options?: { mergeAnonymousData?: boolean }
  ) => Promise<void>;
  hydrateSessionFromToken: (
    token: string,
    options?: { mergeAnonymousData?: boolean }
  ) => Promise<void>;
  clearSession: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

function readStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawSession = localStorage.getItem(AUTH_SESSION_KEY);
  if (!rawSession) {
    return null;
  }

  try {
    const parsedSession = JSON.parse(rawSession) as StoredSession;
    if (!parsedSession?.token || !parsedSession?.user?._id) {
      localStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }

    return parsedSession;
  } catch (error) {
    localStorage.removeItem(AUTH_SESSION_KEY);
    return null;
  }
}

function ensureAnonymousUserId() {
  if (typeof window === 'undefined') {
    return null;
  }

  let storedUserId = localStorage.getItem(ANONYMOUS_USER_ID_KEY);
  if (!storedUserId) {
    storedUserId = uuidv4();
    localStorage.setItem(ANONYMOUS_USER_ID_KEY, storedUserId);
  }

  return storedUserId;
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [anonymousUserId, setAnonymousUserId] = useState<string | null>(null);
  const [session, setSession] = useState<StoredSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const nextAnonymousUserId = ensureAnonymousUserId();
    const nextSession = readStoredSession();

    setAnonymousUserId(nextAnonymousUserId);
    setSession(nextSession);
    setIsLoading(false);
  }, []);

  const syncAuthSession = useCallback(
    async (
      nextSession: AuthResponse | StoredSession,
      options: { mergeAnonymousData?: boolean } = {}
    ) => {
      const normalizedSession: StoredSession = {
        token: nextSession.token,
        user: nextSession.user,
      };

      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(normalizedSession));
      setSession(normalizedSession);

      const shouldMergeAnonymousData = options.mergeAnonymousData !== false;
      if (
        shouldMergeAnonymousData &&
        anonymousUserId &&
        anonymousUserId !== normalizedSession.user._id
      ) {
        try {
          await linkAnonymousUserData(normalizedSession.token, anonymousUserId);
        } catch (error) {
          console.error('Unable to link anonymous user data:', error);
        }
      }
    },
    [anonymousUserId]
  );

  const hydrateSessionFromToken = useCallback(
    async (token: string, options: { mergeAnonymousData?: boolean } = {}) => {
      const user = await fetchUserProfile(token);
      await syncAuthSession({ token, user }, options);
    },
    [syncAuthSession]
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem(AUTH_SESSION_KEY);
    setSession(null);

    const nextAnonymousUserId = ensureAnonymousUserId();
    setAnonymousUserId(nextAnonymousUserId);
  }, []);

  const value = useMemo<UserContextValue>(() => {
    const currentUserId = session?.user?._id || anonymousUserId;

    return {
      userId: currentUserId,
      user: session?.user || null,
      token: session?.token || null,
      anonymousUserId,
      isAuthenticated: Boolean(session?.token && session?.user?._id),
      isLoading,
      syncAuthSession,
      hydrateSessionFromToken,
      clearSession,
    };
  }, [anonymousUserId, clearSession, hydrateSessionFromToken, isLoading, session, syncAuthSession]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useAuth must be used within a UserProvider');
  }

  return context;
};

export const useUserId = () => useAuth().userId;
